import { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  executed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-100 text-slate-700",
  failed: "bg-red-100 text-red-800",
  pending: "bg-amber-100 text-amber-800",
};

/**
 * Drawer showing the full stored AI action (params, result, errors).
 * Includes a "Create macro from this action" shortcut to capture winning prompts.
 */
export const AIActionDetailDrawer = ({ actionId, open, onOpenChange, onMacroCreated }) => {
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState(null);
  const [macroMode, setMacroMode] = useState(false);
  const [macroForm, setMacroForm] = useState({ title: "", emoji: "⚡" });
  const [savingMacro, setSavingMacro] = useState(false);

  useEffect(() => {
    if (!open || !actionId) { setRec(null); setMacroMode(false); return; }
    setLoading(true);
    setMacroMode(false);
    api.aiActionDetail(actionId)
      .then((r) => {
        setRec(r.data);
        // Pre-fill macro title from Claude's summary so 1-click save is possible
        setMacroForm({
          title: (r.data?.summary || "").slice(0, 60) || "Macro",
          emoji: "⚡",
        });
      })
      .catch(() => setRec({ error_loading: "Impossible de charger" }))
      .finally(() => setLoading(false));
  }, [actionId, open]);

  const createMacro = async () => {
    if (!rec?.original_text) return;
    if (!macroForm.title.trim()) { toast.error("Titre requis"); return; }
    setSavingMacro(true);
    try {
      await api.aiCreateMacro({
        title: macroForm.title.trim(),
        prompt: rec.original_text,
        emoji: macroForm.emoji || "⚡",
        auto_execute: false,
      });
      toast.success("Macro créée");
      setMacroMode(false);
      if (typeof onMacroCreated === "function") onMacroCreated();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setSavingMacro(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[min(640px,100vw)] overflow-y-auto" data-testid="ai-action-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading">Détail de l'action IA</SheetTitle>
          <SheetDescription>
            {rec?.original_text ? `"${rec.original_text}"` : "—"}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4 text-sm">
          {loading && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          {rec && !loading && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={STATUS_COLORS[rec.status] || "bg-slate-100"}>{rec.status}</Badge>
                <Badge variant="outline">{rec.action_type}</Badge>
                <Badge variant="outline" className="font-mono">
                  {Math.round((rec.confidence || 0) * 100)}% confiance
                </Badge>
                {rec.original_text && rec.action_type !== "none" && !macroMode && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                    onClick={() => setMacroMode(true)}
                    data-testid="ai-drawer-create-macro"
                  >
                    <Zap className="h-4 w-4 mr-1 text-primary" /> Créer une macro
                  </Button>
                )}
              </div>

              {macroMode && (
                <div className="border-2 border-primary/30 rounded-md p-3 bg-primary/5 space-y-2" data-testid="ai-drawer-macro-form">
                  <div className="text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-1">
                    <Zap className="h-3.5 w-3.5" /> Nouvelle macro
                  </div>
                  <div className="flex gap-2">
                    <Input
                      className="w-16"
                      value={macroForm.emoji}
                      onChange={(e) => setMacroForm((f) => ({ ...f, emoji: e.target.value.slice(0, 3) }))}
                      maxLength={3}
                      data-testid="ai-drawer-macro-emoji"
                    />
                    <Input
                      className="flex-1"
                      placeholder="Titre de la macro"
                      value={macroForm.title}
                      onChange={(e) => setMacroForm((f) => ({ ...f, title: e.target.value }))}
                      data-testid="ai-drawer-macro-title"
                    />
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Prompt réutilisé : <span className="italic">"{rec.original_text}"</span>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setMacroMode(false)} disabled={savingMacro}>
                      Annuler
                    </Button>
                    <Button size="sm" onClick={createMacro} disabled={savingMacro || !macroForm.title.trim()} data-testid="ai-drawer-macro-save">
                      {savingMacro && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Enregistrer
                    </Button>
                  </div>
                </div>
              )}

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <dt className="text-muted-foreground uppercase tracking-wider">Créé le</dt>
                  <dd className="font-mono">{rec.created_at ? new Date(rec.created_at).toLocaleString("fr-FR") : "—"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground uppercase tracking-wider">Par</dt>
                  <dd>{rec.created_by_name || rec.created_by_email || "—"}</dd>
                </div>
                {rec.executed_at && (
                  <>
                    <div>
                      <dt className="text-muted-foreground uppercase tracking-wider">Exécuté le</dt>
                      <dd className="font-mono">{new Date(rec.executed_at).toLocaleString("fr-FR")}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground uppercase tracking-wider">Exécuté par</dt>
                      <dd>{rec.executed_by_name || rec.executed_by_email || "—"}</dd>
                    </div>
                  </>
                )}
              </dl>

              {rec.summary && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Résumé</div>
                  <div className="italic">{rec.summary}</div>
                </div>
              )}

              {rec.ambiguities?.length > 0 && (
                <div className="border-l-4 border-amber-400 bg-amber-50 p-2 text-xs">
                  <div className="font-semibold text-amber-800">Ambiguïtés</div>
                  <ul className="list-disc ml-4">{rec.ambiguities.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </div>
              )}

              {rec.warnings?.length > 0 && (
                <div className="border-l-4 border-slate-400 bg-slate-50 p-2 text-xs">
                  <div className="font-semibold">Avertissements</div>
                  <ul className="list-disc ml-4">{rec.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}

              {rec.error && (
                <div className="border-l-4 border-red-500 bg-red-50 p-2 text-xs text-red-900">
                  <div className="font-semibold">Erreur d'exécution</div>
                  <pre className="whitespace-pre-wrap mt-1">{rec.error}</pre>
                </div>
              )}

              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Params</div>
                <pre className="bg-muted/40 p-2 rounded text-[11px] font-mono whitespace-pre-wrap overflow-x-auto">
                  {JSON.stringify(rec.params || {}, null, 2)}
                </pre>
              </div>

              {rec.result && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Résultat</div>
                  <pre className="bg-emerald-50 p-2 rounded text-[11px] font-mono whitespace-pre-wrap overflow-x-auto text-emerald-900">
                    {JSON.stringify(rec.result, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
          {rec?.error_loading && <p className="text-red-600">{rec.error_loading}</p>}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AIActionDetailDrawer;
